<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      In this activity, you will work through the game development process and complete 3 tasks.
    </p>
    <p class="mb-5">
      Watch the following video to get an insight into some of the basic principles of game design.
    </p>

    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <p class="mb-5">
      In the video, the presenter identifies 3 main pillars or foundations of good game design as:
    </p>

    <p class="mb-5 text-bold text-italic">1. Player:</p>
    <p class="mb-5">
      What is the player’s role, and how does he or she interact with the game? The player must have
      a purpose, and their actions should be what drives the game.
    </p>

    <p class="mb-5 text-bold text-italic">2. Communication</p>
    <p class="mb-5">
      Once the purpose is established, it must be communicated to the player. This can be done by
      leaving visual cues in the environment that direct the player towards what they need to do.
    </p>

    <p class="mb-5 text-bold text-italic">3. Appeal</p>
    <p class="mb-5">
      The most important part of the foundation. If the game does not appeal to the player, then….
      What’s the point? This is hard because it is solely based on a feeling of being drawn to the
      game and wanting to play more. Furthermore, appeal can greatly vary depending on the game and
      the person playing it. Also, appeal can mean something different depending on the type of
      game. For example, for some it is the action paced realism with stunning graphics and
      heart-throbbing sound, while for others it’s the interesting story or well thought out puzzle.
    </p>

    <p class="mb-5">
      Whether you are working as a small indie games developer or working for a large studio, the
      game development process will remain largely the same and will consist of the following 3 main
      stages:
    </p>

    <ol class="numbered-list">
      <li>Pre-Production</li>
      <li>Production</li>
      <li>Post Production</li>
    </ol>

    <p class="mb-5">
      Let’s take a look at each of these stages in a little more detail and undertake some tasks
      that will give you an insight into how the game development process works and a chance to
      practise some of the related skills.
    </p>
    <h4 class="mb-5">Pre-Production</h4>
    <p class="mb-5">
      This stage defines your game, how long it will take to complete, how many people and resources
      you’ll need, and how much it will cost to produce the game.
    </p>
    <p class="mb-5">
      Depending on the process’s size and complexity, this stage can last from 1 week to over a
      year. It will usually amount to about 10 to 20% of the total game development time.
    </p>
    <p class="mb-5">The 3 main components of the pre-production stage are:</p>

    <centered-image-wrapper
      :image="image1"
      alt="Flow diagram of the three pre-production components: The Concept, then The Plan, then The Prototype."
    />

    <p class="mb-5">Now, let’s take a look at each of these in turn.</p>
    <p class="mb-5 text-underline">The Concept</p>
    <p class="mb-5">
      The order of how each stage of pre-production, production, and post-production can vary
      between game developers, but the following video will give you a fantastic overview of many of
      the most important aspects of how to come up with ideas for video games, as well as how to
      organise the planning stage.
    </p>
    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />

    <p class="mb-5">
      Don’t worry if you haven’t taken in all that information. It’s quite a long video, so we will
      be returning to sections of it to refresh your memory throughout the next few activities.
    </p>
    <p class="mb-5">
      For your reference, though, here is a list of the video’s content with timestamps to help you
      locate useful information while working through the activities.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <p class="mb-5"><strong>0:00</strong> - Intro and Overview</p>
        <p class="mb-5"><strong>2:11</strong> - Step 1: Brainstorm Gameplay Ideas</p>
        <p class="mb-5"><strong>3:37</strong> - Step 2: Create a Gameplay Prototype</p>
        <p class="mb-5"><strong>4:54</strong> - Step 3: Experiment with the Prototype</p>
        <p class="mb-5"><strong>5:41</strong> - Step 4: Determine the Game’s Scale</p>
        <p class="mb-5"><strong>8:25</strong> - Step 5: Brainstorm Story Ideas</p>
        <p class="mb-5"><strong>9:27</strong> - Step 6: Create a Character Bible</p>
        <p class="mb-5"><strong>10:42</strong> - Step 7: Outline the Story Events</p>
        <p class="mb-5"><strong>12:15</strong> - Step 8: Make a Story Exposition List</p>
        <p class="mb-5"><strong>13:21</strong> - Step 9: Expand and Refine the Story Outline</p>
        <p class="mb-5"><strong>14:00</strong> - Step 10: Refine the Game’s Core</p>
        <p class="mb-5"><strong>15:00</strong> - Step 11: Create a List of Level Ideas</p>
        <p class="mb-5"><strong>16:29</strong> - Step 12: Write Your Story</p>
        <p class="mb-5"><strong>17:35</strong> - Step 13: Create a Debug Mode</p>
        <p class="mb-5"><strong>18:40</strong> - Step 14: Plan the Intro Level</p>
        <p class="mb-5"><strong>20:40</strong> - Step 15: Plan the Intro Boss</p>
        <p class="mb-5"><strong>21:22</strong> - Step 16: Plan the Intro Cutscene</p>
        <p class="mb-5">
          <strong>23:00</strong> - Step 17: Revise, Then Exit the Pre-Production Phase
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <img :src="image2" class="img-responsive mx-auto" alt="" />
      </div>
    </div>

    <h4 class="mb-5">Task 1: Choosing a Genre</h4>
    <p class="mb-5">
      Deciding on the type or genre of the game you will create is an excellent place to start.
    </p>
    <p class="mb-5">
      Video games come in many flavours. Watch the following video to gain some insight into the
      main genres of video games.
    </p>
    <responsive-youtube-wrapper :video="videos[3].video" :image="videos[3].image" />

    <p class="mb-5">
      One thing you’ll have noticed about discussing video game genres is that lots of acronyms are
      used. Take a look at the acronyms below. Can you choose what each stands for in the space
      provided?
    </p>
    <div v-for="(question, index) in questions_1" :key="'1_' + index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div
          class="vx-col w-full mb-2"
          v-if="isEditable"
          :class="{
            wrong:
              answers['question' + index] > 0 && question.correct != answers['question' + index],
          }"
        >
          <select
            class="default-select"
            v-model="answers['question' + index]"
            :name="'question_' + index"
            :aria-label="question.question"
          >
            <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
              {{ answer.answer }}
            </option>
          </select>
        </div>
        <div class="my-2 mx-5" v-else>
          <div v-if="answers['question' + index] != 0 && answers['question' + index] != null">
            <p class="my-2" v-for="answer in question.answers" :key="answer.val">
              <span v-if="answers['question' + index] == answer.val">{{ answer.answer }}</span>
            </p>
          </div>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <h4 class="mb-5">Brainstorming Ideas</h4>

    <p class="mb-5">
      Refer back to this 1½ minute segment of the How to Plan a Video Game video to refresh your
      memory on some ways to brainstorm video game ideas.
    </p>
    <p class="mb-5"><strong>2:11</strong> - Step 1: Brainstorm Gameplay Ideas</p>
    <p class="mb-5">
      Now, let’s explore one of these brainstorming techniques in a little more detail.
    </p>
    <h4 class="mb-5">Task 2: Quiz: What Type of Video Game Suits You?</h4>
    <p class="mb-5">
      Sometimes a good way to decide on a video genre is to choose a genre that appeals to you.
      Especially when starting.
    </p>
    <p class="mb-5">
      Choosing a video genre that you enjoy is one way to help you maintain interest throughout the
      production process.
    </p>
    <p class="mb-5">
      To help you identify the types of games that might suit you best, you can participate in the
      fun, interactive quiz below. Just be sure to have a pencil and paper handy to record and add
      up your scores as you go (you can pause the video along the way if you need to).
    </p>
    <responsive-youtube-wrapper :video="videos[4].video" :image="videos[4].image" />

    <p class="mb-5">
      How did you do? Based on your score, what type of video game was suggested for you by the
      quiz?
    </p>

    <div v-for="(question, index) in questions_2" :key="'2_' + index">
      <div class="vx-row my-5">
        <div class="vx-col w-full mb-2" v-if="isEditable">
          <select
            class="default-select"
            v-model="answers['question6']"
            :name="'question_6'"
            aria-label="Choose the video game type that was recommended for you"
          >
            <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
              {{ answer.answer }}
            </option>
          </select>
        </div>
        <div class="my-2 mx-5" v-else>
          <div v-if="answers['question6'] != 0 && answers['question6'] != null">
            <p class="my-2" v-for="answer in question.answers" :key="answer.val">
              <span v-if="answers['question6'] == answer.val">{{ answer.answer }}</span>
            </p>
          </div>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <h4 class="mb-5">The Ultimate List of Different Types of Video Games</h4>
    <p class="mb-5">
      Finally, to help you choose which genre you’d like to explore further, here’s a very
      informative article to read on the many different types of video games.
    </p>
    <p class="mb-5">
      In this article, you’ll learn about the main types of video games and many subcategories. The
      main genres looked at are:
    </p>

    <div class="vx-row my-5 overflow-hidden">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <ul class="default-list xl:float-right lg:float-right">
          <li>Action games</li>
          <li>Action-adventure games</li>
          <li>Adventure games</li>
          <li>Role-playing games</li>
          <li>Simulation games</li>
          <li>Strategy games</li>
          <li>Sports games</li>
          <li>Puzzle games</li>
          <li>Idle games</li>
        </ul>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <a
          href="https://www.idtech.com/blog/different-types-of-video-game-genres"
          target="_blank"
          class="xl:float-left lg:float-left"
        >
          <img
            :src="image3"
            alt="Read about the different types of video game genres on idTech (opens in a new tab)"
            class="img-responsive"
            style="max-height: 300px"
          />
        </a>
      </div>
    </div>

    <h4 class="mb-5">Task 3: Make Your Choice</h4>
    <p class="mb-5">
      Now you’ve had some time to explore the different genres of video games and to take a quiz to
      identify a type of video game potentially suited to your personality; it’s time to decide
      which type of video game you’re going to choose to work on in the next activities.
    </p>
    <p class="mb-5">Record that genre below:</p>

    <div v-for="(question, index) in questions_3" :key="'2_' + index">
      <div class="vx-row my-5">
        <div class="vx-col w-full mb-2" v-if="isEditable">
          <select
            class="default-select"
            v-model="answers['question7']"
            :name="'question7'"
            aria-label="Choose the video game genre that was recommended for you"
          >
            <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
              {{ answer.answer }}
            </option>
          </select>
        </div>
        <div class="my-2 mx-5" v-else>
          <div v-if="answers['question7'] != 0 && answers['question7'] != null">
            <p class="my-2" v-for="answer in question.answers" :key="answer.val">
              <span v-if="answers['question7'] == answer.val">{{ answer.answer }}</span>
            </p>
          </div>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
//import methods from "@/views/WorkExperiences/components/activity_js/methods";
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/gamesdeveloper/basic_principles_of_game_design_splash.jpg'),
          video: 'https://www.youtube.com/embed/G8AT01tuyrk',
        },
        2: {
          image: require('@/assets/images/experiences/gamesdeveloper/how_to_plan_a_video_game_splash.jpg'),
          video: 'https://www.youtube.com/embed/_kx_mpRyVMM',
        },
        3: {
          image: require('@/assets/images/experiences/gamesdeveloper/video_game_genres_splash.jpg'),
          video: 'https://www.youtube.com/embed/_F6rnWGR-_w',
        },
        4: {
          image: require('@/assets/images/experiences/gamesdeveloper/what_type_of_video_game_suits_you_splash.jpg'),
          video: 'https://www.youtube.com/embed/_286IPSNGQI',
        },
      },
      image1: require('@/assets/images/experiences/gamesdeveloper/activity_1_1.png'),
      image2: require('@/assets/images/experiences/gamesdeveloper/activity_1_2.jpg'),
      image3: require('@/assets/images/experiences/gamesdeveloper/video_game_type_article_button.jpg'),
      questions_1: {
        1: {
          question: 'FPS',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 3, answer: 'Massively Multiplayer Online game' },
            { val: 2, answer: 'Role-Playing Game game' },
            { val: 1, answer: 'First-Person Shooter game' },
            { val: 4, answer: 'Multiplayer Online Battle Arena game' },
          ],
          correct: 1,
        },
        2: {
          question: 'RPG',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 3, answer: 'Massively Multiplayer Online game' },
            { val: 2, answer: 'Role-Playing Game game' },
            { val: 1, answer: 'First-Person Shooter game' },
            { val: 4, answer: 'Multiplayer Online Battle Arena game' },
          ],
          correct: 2,
        },
        3: {
          question: 'MMO',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 3, answer: 'Massively Multiplayer Online game' },
            { val: 2, answer: 'Role-Playing Game game' },
            { val: 1, answer: 'First-Person Shooter game' },
            { val: 4, answer: 'Multiplayer Online Battle Arena game' },
          ],
          correct: 3,
        },
        4: {
          question: 'MOBA',
          answers: [
            { val: undefined, answer: 'Please Select' },
            { val: 3, answer: 'Massively Multiplayer Online game' },
            { val: 2, answer: 'Role-Playing Game game' },
            { val: 1, answer: 'First-Person Shooter game' },
            { val: 4, answer: 'Multiplayer Online Battle Arena game' },
          ],
          correct: 4,
        },
      },
      questions_2: {
        1: {
          answers: [
            {
              val: undefined,
              answer: 'Please choose the video game type that was recommended for you',
            },
            { val: 1, answer: 'Arcade Games' },
            { val: 2, answer: 'Battle Royale Games' },
            { val: 3, answer: 'Role Player Games' },
            { val: 4, answer: 'Action-Adventure Games' },
            { val: 5, answer: 'Simulator Games' },
            { val: 6, answer: 'Racing Games' },
          ],
        },
      },
      questions_3: {
        1: {
          answers: [
            {
              val: undefined,
              answer: 'Please choose the video game type that was recommended for you',
            },
            { val: 1, answer: 'Arcade Games' },
            { val: 2, answer: 'Battle Royale Games' },
            { val: 3, answer: 'Action-Adventure Games' },
            { val: 4, answer: 'Simulator Games' },
            { val: 5, answer: 'Racing Games' },
            { val: 6, answer: 'Action games' },
            { val: 7, answer: 'Adventure games' },
            { val: 8, answer: 'Role-playing games' },
            { val: 9, answer: 'Simulation games' },
            { val: 10, answer: 'Strategy games' },
            { val: 11, answer: 'Sports games' },
            { val: 12, answer: 'Puzzle games' },
            { val: 13, answer: 'Idle games' },
          ],
        },
      },
      answers: {
        question1: undefined,
        question2: undefined,
        question3: undefined,
        question4: undefined,
        question5: undefined,
        question6: undefined,
        question7: undefined,
      },
      correct_answers: {
        question1: true,
        question2: true,
        question3: true,
        question4: true,
        question5: true,
      },
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.data !== 'undefined') {
      this.answers = this.data;
    }
  },
};
</script>

<style scoped>
label.isbar_label span {
  color: #d40000;
  font-weight: bold;
}

.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
  margin: 0;
}
</style>
